// Angular imports
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';

// 3rd party imports
import { DxValidationGroupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// CC-Common imports
import { CCLocalStorageService, IconConstants, Utils } from 'cc-common';

// Service imports
import { ScreenService } from '../_services/shared-services/screen.service';
import { ServerErrorService } from '../_services/shared-services/server-error.service';
import { SearchService } from './_services/data-services/search.service';
import { CommunityService } from '../_services/data-services/community.service';
import { HelperService } from '../_services/shared-services/helper.service';
import { CiraHelpService } from '../_services/shared-services/cira-help.service';

// Component imports
import { MPBaseComponentWithGrid } from '../_base/mp-base-component-with-grid';
import { SearchPropertiesCurrentOwnersComponent } from './search-properties-current-owners/search-properties-current-owners.component';
import { SearchPropertiesAllOwnersComponent } from './search-properties-all-owners/search-properties-all-owners.component';
import { SearchProjectTasksComponent } from './search-project-tasks/search-project-tasks.component';
import { SearchWorkOrdersComponent } from './search-work-orders/search-work-orders.component';
import { SearchVendorDisbursementsCheckComponent } from './search-vendor-disbursements-check/search-vendor-disbursements-check.component';
import { SearchInvoicesWorkflowComponent } from './search-invoices-workflow/search-invoices-workflow.component';
import { SearchViolationNoticesComponent } from './search-violation-notices/search-violation-notices.component';
import { SearchVendorsComponent } from './search-vendors/search-vendors.component';
import { SearchArchitecturalApplicationsComponent } from './search-architectural-applications/search-architectural-applications.component';
import { MultiColumnSelectorComponent } from '../management-common/component/common/multi-column-selector/multi-column-selector.component';
import { ICommunityContextDashboardComponent } from '../dashboard-module/_interfaces/community-context-component';
import { IInteractiveDashboardComponent } from '../dashboard-module/_interfaces/interactive-dashboard-component';

// Model imports
import { CommunitySelectorResultDto } from '../_models/community-selector-result-dto';
import { BaseParameterDto } from '../_models/base-parameter-dto';
import { SearchTypeResultDto } from './_models/search-type-result-dto';

// Constant imports
import { SearchTypesConstants } from './_constants/search.constants';
import { MessageInterval } from '../_constants/messages-intervals';
import { CiraHelpKeyConstants } from '../_constants/cira-help-key-constants';

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss']
})
export class SearchComponent
    extends MPBaseComponentWithGrid
    implements OnInit, ICommunityContextDashboardComponent, IInteractiveDashboardComponent
{
    //#region Component Variables

    @ViewChild('currentOwners', { static: false }) currentOwners: SearchPropertiesCurrentOwnersComponent;
    @ViewChild('allOwners', { static: false }) allOwners: SearchPropertiesAllOwnersComponent;
    @ViewChild('projectTasks', { static: false }) projectTasks: SearchProjectTasksComponent;
    @ViewChild('workOrders', { static: false }) workOrders: SearchWorkOrdersComponent;
    @ViewChild('vendorDisbursementsCheck', { static: false })
    vendorDisbursementsCheck: SearchVendorDisbursementsCheckComponent;

    @ViewChild('invoicesWorkflow', { static: false }) invoicesWorkflow: SearchInvoicesWorkflowComponent;
    @ViewChild('violationNotices', { static: false }) violationNotices: SearchViolationNoticesComponent;
    @ViewChild('vendors', { static: false }) vendors: SearchVendorsComponent;
    @ViewChild('architecturalApplications', { static: false })
    architecturalApplications: SearchArchitecturalApplicationsComponent;

    @ViewChild('seachValidator', { static: false }) seachValidator: DxValidationGroupComponent;
    @ViewChild('communitySelector', { static: false }) communitySelector: MultiColumnSelectorComponent;

    hideTitle = false;
    minWidthRatio = 30;
    minHeightRatio = 30;
    minWidth = 990;
    minHeight = 600;
    hideFullScreen = false;
    widgetId: string;

    isSmallScreen: boolean;
    loadingVisible = false;
    searchIcon: string = IconConstants.SEARCH;
    searchTypes: SearchTypeResultDto[] = [];
    allCommunitiesList: CommunitySelectorResultDto[] = [];
    communityList: CommunitySelectorResultDto[] = [];

    selectedSearchTypeCode: number = SearchTypesConstants.propertiesCurrentOwners;
    selectedCommunityId: number;
    searchText: string;
    communityColumns: any[];
    communityGridBoxValue: number[];

    searchType: any = SearchTypesConstants;
    clearSearchButton: object;
    isCommunitySelectorHidden = false;

    helpLink: string;
    startDate: Date = new Date();
    endDate: Date = new Date();

    filterColCount = 6;

    //#endregion

    //#region Component life cycle methods

    constructor(
        protected readonly screenService: ScreenService,
        protected readonly localStorageService: CCLocalStorageService,
        protected readonly helperService: HelperService,
        private readonly route: ActivatedRoute,
        private readonly errorService: ServerErrorService,
        private readonly searchService: SearchService,
        private readonly communityService: CommunityService,
        private readonly ciraHelpService: CiraHelpService
    ) {
        super(screenService, localStorageService, helperService);

        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.advancedSearch);

        this.onSearchClick = this.onSearchClick.bind(this);

        this.communityColumns = this.communityService.getCommunitySelectorColumns();
        this.route.queryParams.subscribe((params: Params) => {
            const searchText = params['search'];
            if (!!searchText) {
                this.searchText = searchText.trim();
            }
            const searchType = params['searchType'];
            if (!!searchType) {
                this.selectedSearchTypeCode = parseInt(searchType);
            }
            const communityId = params['communityId'];
            if (!!communityId) {
                this.selectedCommunityId = Number(communityId);
            }
        });
    }

    ngOnInit(): void {
        this.startDate.setMonth(this.startDate.getMonth() - 1);
        this.isSmallScreen = this.screenService.isSmallScreen();

        this.screenService.changed.subscribe(() => {
            this.isSmallScreen = this.screenService.isSmallScreen();
        });

        this.getSearchTypes();
        this.getCommunitySelector();
    }

    //#endregion

    //#region Event Handlers

    onSearchClick(): void {
        if (Utils.notNullAndDefined(this.searchText)) {
            if (Utils.notNullAndDefined(this.selectedCommunityId)) {
                if (this.searchText.length > 1) {
                    this.search();
                } else {
                    notify(
                        'Please provide at least 2 characters to search with community',
                        'error',
                        MessageInterval.defaultErrorMsgInterval
                    );
                }
            } else if (this.searchText.length > 2) {
                this.search();
            } else {
                notify(
                    'Please provide at least 3 characters to search',
                    'error',
                    MessageInterval.defaultErrorMsgInterval
                );
            }
        } else {
            notify('Please provide at least 3 characters to search', 'error', MessageInterval.defaultErrorMsgInterval);
        }
    }

    onCommunitySelection(selectedObject: any): void {
        this.selectedCommunityId = null;
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const selectedRows: any[] = selectedObject.selectedRow;
            this.selectedCommunityId = selectedRows[0].communityId;
            this.search();
        }
    }

    onSearchTextboxKeyUp($event: any) {
        const text = $event.event.target.value;
        this.searchText = text;
        if (this.searchText === '') {
            this.search();
        }
    }

    onSearchTypeChanged(): void {
        if (!!this.allCommunitiesList) {
            if (this.selectedSearchTypeCode === SearchTypesConstants.propertiesAllOwners) {
                this.communityList = this.allCommunitiesList;
            } else {
                this.communityList = this.allCommunitiesList.filter((r) => r.isActive);
            }
        }
        if (Utils.notNullOrEmpty(this.communityList) && Utils.equals(this.communityList.length, 1)) {
            this.communityGridBoxValue = [this.communityList[0].communityId];
        }
    }

    //#endregion

    //#region Overriden Methods

    refresh(width: number, height: number): void {
        if (width < this.minWidth) {
            const colCount = Utils.equals(Math.round(width / 200), 5) ? 6 : Math.round(width / 200);
            this.filterColCount = colCount;
        }
    }

    getTitle(name: string): string {
        return name;
    }

    setCommunity(communityId: number, managementCompanyId: number, communityName: string): void {
        if (!!communityId) {
            this.selectedCommunityId = communityId;
            this.communityGridBoxValue = [communityId];
            this.search();
        } else {
            this.communityGridBoxValue = [];
            this.selectedCommunityId = null;
            if (!!this.communitySelector) {
                this.communitySelector.resetSelector();
            }
            this.search();
        }
    }

    //#endregion

    getSearchForColColunt(): number {
        if (this.filterColCount >= 6) {
            return 3;
        } else if (Utils.isInList(this.filterColCount, [3, 4]) && Utils.isTrue(this.showDateFields())) {
            return 1;
        } else if (Utils.equals(this.filterColCount, 2)) {
            return 1;
        } else {
            return 2;
        }
    }

    getScreenByWidth = (width) => {
        return this.isSmallScreen ? 'sm' : 'lg';
    };

    communityDisplayExpr(item: any): string {
        let displayText: string;
        if (!!item) {
            displayText = `${item.communityName}, ${item.locationName}, ${item.managementCompanyName}`;
        }
        return displayText;
    }

    showDateFields(): boolean {
        return (
            this.searchType.projectTasks === this.selectedSearchTypeCode ||
            this.searchType.invoicesWorkflow === this.selectedSearchTypeCode ||
            this.searchType.workOrders === this.selectedSearchTypeCode
        );
    }

    //#region Private Methods

    private search(): void {
        this.clearSearch();

        if (
            Utils.notNullAndDefined(this.searchText) &&
            ((this.searchText.length > 1 && Utils.notNullAndDefined(this.selectedCommunityId)) ||
                (this.searchText.length > 2 && Utils.isNullOrUndefined(this.selectedCommunityId)))
        ) {
            switch (this.selectedSearchTypeCode) {
                case SearchTypesConstants.propertiesCurrentOwners:
                    if (!!this.currentOwners) {
                        this.currentOwners.communityId = this.selectedCommunityId;
                        this.currentOwners.searchText = this.searchText;
                        this.currentOwners.searchCurrentOwners();
                    }
                    break;
                case SearchTypesConstants.propertiesAllOwners:
                    if (!!this.allOwners) {
                        this.allOwners.communityId = this.selectedCommunityId;
                        this.allOwners.searchText = this.searchText;
                        this.allOwners.searchAllOwners();
                    }
                    break;
                case SearchTypesConstants.projectTasks:
                    if (!!this.projectTasks) {
                        this.projectTasks.communityId = this.selectedCommunityId;
                        this.projectTasks.searchText = this.searchText;
                        this.projectTasks.searchProjectTasks();
                    }
                    break;
                case SearchTypesConstants.workOrders:
                    if (!!this.workOrders) {
                        this.workOrders.communityId = this.selectedCommunityId;
                        this.workOrders.searchText = this.searchText;
                        this.workOrders.searchWorkOrders();
                    }
                    break;
                case SearchTypesConstants.vendorDisbursementsCheck:
                    if (!!this.vendorDisbursementsCheck) {
                        this.vendorDisbursementsCheck.communityId = this.selectedCommunityId;
                        this.vendorDisbursementsCheck.searchText = this.searchText;
                        this.vendorDisbursementsCheck.searchVendorDisbursementsCheck();
                    }
                    break;
                case SearchTypesConstants.invoicesWorkflow:
                    if (!!this.invoicesWorkflow) {
                        this.invoicesWorkflow.communityId = this.selectedCommunityId;
                        this.invoicesWorkflow.searchText = this.searchText;
                        this.invoicesWorkflow.searchInvoicesWorkflow();
                    }
                    break;
                case SearchTypesConstants.vendors:
                    if (!!this.vendors) {
                        this.vendors.communityId = this.selectedCommunityId;
                        this.vendors.searchText = this.searchText;
                        this.vendors.searchVendors();
                    }
                    break;
                case SearchTypesConstants.architecturalApplications:
                    if (!!this.architecturalApplications) {
                        this.architecturalApplications.communityId = this.selectedCommunityId;
                        this.architecturalApplications.searchText = this.searchText;
                        this.architecturalApplications.searchArchitecturalApplications();
                    }
                    break;
                case SearchTypesConstants.violationNotices:
                    if (!!this.violationNotices) {
                        this.violationNotices.communityId = this.selectedCommunityId;
                        this.violationNotices.searchText = this.searchText;
                        this.violationNotices.searchviolationNotices();
                    }
                    break;
            }
        }
    }

    private clearSearch(): void {
        switch (this.selectedSearchTypeCode) {
            case SearchTypesConstants.propertiesCurrentOwners:
                if (!!this.currentOwners) {
                    this.currentOwners.propertyCurrentOwners = [];
                }
                break;
            case SearchTypesConstants.propertiesAllOwners:
                if (!!this.allOwners) {
                    this.allOwners.propertyAllOwners = [];
                }
                break;
            case SearchTypesConstants.projectTasks:
                if (!!this.projectTasks) {
                    this.projectTasks.projectTasks = [];
                }
                break;
            case SearchTypesConstants.workOrders:
                if (!!this.workOrders) {
                    this.workOrders.workOrders = [];
                }
                break;
            case SearchTypesConstants.vendorDisbursementsCheck:
                if (!!this.vendorDisbursementsCheck) {
                    this.vendorDisbursementsCheck.vendorDisbursements = [];
                }
                break;
            case SearchTypesConstants.invoicesWorkflow:
                if (!!this.invoicesWorkflow) {
                    this.invoicesWorkflow.invoiceWorkflows = [];
                }
                break;
            case SearchTypesConstants.vendors:
                if (!!this.vendors) {
                    this.vendors.vendors = [];
                }
                break;
            case SearchTypesConstants.architecturalApplications:
                if (!!this.architecturalApplications) {
                    this.architecturalApplications.architecturalApplications = [];
                }
                break;
            case SearchTypesConstants.violationNotices:
                if (!!this.violationNotices) {
                    this.violationNotices.violationNotices = [];
                }
                break;
        }
    }

    private getSearchTypes(): void {
        const param: BaseParameterDto = new BaseParameterDto();
        param.clearCache = true;
        this.loadingVisible = true;
        this.searchService.getSearchTypes(param).subscribe(
            (data) => {
                this.searchTypes = data;
                if (!this.selectedSearchTypeCode) {
                    this.selectedSearchTypeCode = data[0].code;
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private getCommunitySelector(): void {
        this.loadingVisible = true;
        this.communityService.getCommunities(false, true).subscribe(
            (data) => {
                this.allCommunitiesList = data;
                this.loadingVisible = false;
                this.configureCommunityId();
                this.onSearchTypeChanged();
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private configureCommunityId(): void {
        if (!!this.selectedCommunityId) {
            this.communityGridBoxValue = [this.selectedCommunityId];
        }
    }

    //#endregion
}
